/**
 * employerBulkPolling.js — Slice β bulk-eval polling helper.
 *
 * Modelled structurally on lib/analyseFitPolling.js (RC 1.48.1 /
 * RC 1.49.7 W.2) — capped exponential back-off, AbortSignal
 * support, visibility-API wake on tab return, terminal short-circuit.
 *
 * Polls GET /api/employer/v1/bulk-eval/jobs/{jobId} only. Does NOT
 * dispatch — dispatch lives in BulkEvalConfirmDialog. Does NOT
 * fetch child items beyond the embedded `items[]` already in the
 * GET response (per Slice β strict-hold: no per-item endpoint).
 *
 * Terminal statuses on the parent: complete | partial | failed.
 */
import { apiFetch } from './api';

const POLL_DELAYS_MS = [2000, 3000, 4000, 6000, 8000];
const MAX_POLLS = 60; // ~6 min ceiling (vs 30 on analyseFit; bulk runs longer)
const TERMINAL = new Set(['complete', 'partial', 'failed']);

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) { reject(makeAbortError()); return; }
    const t = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(t);
      reject(makeAbortError());
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

function makeAbortError() {
  if (typeof DOMException === 'function') {
    return new DOMException('Polling aborted', 'AbortError');
  }
  const e = new Error('Polling aborted');
  e.name = 'AbortError';
  return e;
}

/**
 * Fetch a single snapshot. Returns the envelope from
 * GET /bulk-eval/jobs/{id}: { job, items, retry_after_ms, poll_url }.
 */
export async function fetchJobSnapshot(jobId, { signal } = {}) {
  if (signal?.aborted) throw makeAbortError();
  return apiFetch(`/api/employer/v1/bulk-eval/jobs/${jobId}`);
}

/**
 * Poll until the parent job reaches a terminal status.
 *
 * @param {string} jobId
 * @param {object} opts
 * @param {AbortSignal} [opts.signal]
 * @param {(snapshot) => void} [opts.onTick]    invoked after every successful poll
 * @returns {Promise<object>} terminal snapshot
 */
export async function pollBulkEvalJob(jobId, opts = {}) {
  const { signal, onTick } = opts;
  if (!jobId) throw new Error('pollBulkEvalJob requires a jobId');

  // Visibility-API wake — if the tab returns from background and the
  // next poll is more than 2s away, poll immediately on the next
  // iteration.
  let visibilityWake = false;
  const onVisibility = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      visibilityWake = true;
    }
  };
  if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
    document.addEventListener('visibilitychange', onVisibility);
  }

  try {
    // Immediate first poll. (Distinguishes from analyseFitPolling
    // which sleeps first — for bulk we want operator-visible state
    // ASAP, and the runMarker rehydration path benefits from an
    // instant first read.)
    let snapshot = await fetchJobSnapshot(jobId, { signal });
    onTick?.(snapshot);
    if (TERMINAL.has(snapshot?.job?.status)) return snapshot;

    for (let i = 0; i < MAX_POLLS; i += 1) {
      if (signal?.aborted) throw makeAbortError();
      const baseDelay = POLL_DELAYS_MS[Math.min(i, POLL_DELAYS_MS.length - 1)];
      const delay = visibilityWake ? 0 : baseDelay;
      visibilityWake = false;
      if (delay > 0) {
        await sleep(delay, signal);
      }
      if (signal?.aborted) throw makeAbortError();

      snapshot = await fetchJobSnapshot(jobId, { signal });
      onTick?.(snapshot);

      if (TERMINAL.has(snapshot?.job?.status)) return snapshot;
    }
    const err = new Error('Bulk evaluation is still running after the maximum wait.');
    err.code = 'POLL_TIMEOUT';
    throw err;
  } finally {
    if (typeof document !== 'undefined' && typeof document.removeEventListener === 'function') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  }
}

// Test-only.
export const __testing = { POLL_DELAYS_MS, MAX_POLLS, TERMINAL };
